import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaTrophy, FaCrown } from 'react-icons/fa';
import api from '../../api/axios';

/**
 * Bannière du défi hebdomadaire post-compétition (26/10 → 15/11/2026) :
 * 5 parties/jour pendant 7 jours CONSÉCUTIFS → 7 jours de Premium offerts.
 * Un jour manqué remet la série à zéro mais on peut relancer une nouvelle
 * série de 7 jours tant que la fenêtre n'est pas close (récompense gagnable
 * une seule fois). Ne s'affiche que pendant la campagne (active=true côté API).
 */
export default function WeeklyChallengeBanner({ className = '' }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/weekly-challenge/me').then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data || !data.active) return null;

  const { streakDays, target, todayCount, todayTarget, rewardGranted } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={className}
    >
      <div
        className="rounded-2xl p-4"
        style={{
          background: 'linear-gradient(135deg, rgba(240,180,41,0.12) 0%, rgba(26,74,46,0.7) 100%)',
          border: '1px solid rgba(240,180,41,0.3)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          {rewardGranted ? <FaCrown size={15} className="text-qfl-gold" /> : <FaTrophy size={15} className="text-qfl-gold" />}
          <span
            className="text-qfl-gold font-black text-sm uppercase tracking-widest"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Défi de la semaine
          </span>
        </div>

        {rewardGranted ? (
          <p className="text-white/80 text-sm">
            🎉 Défi réussi — <strong className="text-qfl-gold">7 jours de Premium</strong> offerts !
          </p>
        ) : (
          <>
            <p className="text-white/60 text-xs mb-3">
              5 parties par jour pendant 7 jours CONSÉCUTIFS → <strong className="text-qfl-gold">7 jours de Premium</strong> offerts. Un jour manqué remet la série à zéro, mais tu peux retenter jusqu'au 15 novembre.
            </p>

            {/* Jalons des 7 jours */}
            <div className="flex justify-between gap-1 mb-3">
              {Array.from({ length: target }, (_, i) => i + 1).map((day) => (
                <div
                  key={day}
                  className="flex-1 h-2 rounded-full"
                  style={{
                    background: streakDays >= day ? '#f0b429' : 'rgba(255,255,255,0.1)',
                  }}
                />
              ))}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-white/50 text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Jour {streakDays}/{target}
              </span>
              <span
                className="text-xs font-bold"
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  color: todayCount >= todayTarget ? '#22c55e' : 'rgba(255,255,255,0.6)',
                }}
              >
                {Math.min(todayCount, todayTarget)}/{todayTarget} parties aujourd'hui
                {todayCount >= todayTarget ? ' ✅' : ''}
              </span>
            </div>

            {/* Le compteur "Jour X/7" n'est mis à jour qu'une fois par nuit (23h55) —
                sans ce message, un joueur qui vient de finir ses 5 parties du jour
                voit "0/7" à côté de "5/5 ✅" et croit que rien n'est pris en compte. */}
            {todayCount >= todayTarget && (
              <p className="text-green-400/80 text-[11px] mt-2 text-center">
                Objectif du jour atteint — ta série sera mise à jour cette nuit 🌙
              </p>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}
